/**
 * AnimatedProgressBar Component
 * 
 * Animated progress bar for funnel visualization.
 * Per design.md: 300ms width transition on data load.
 */

import { motion } from 'framer-motion';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { cn } from '@uprosal/ui';

interface AnimatedProgressBarProps {
    value: number;
    maxValue: number;
    className?: string;
    barClassName?: string;
    delay?: number;
    height?: string;
}

export function AnimatedProgressBar({
    value,
    maxValue,
    className,
    barClassName,
    delay = 0,
    height = 'h-3',
}: AnimatedProgressBarProps) {
    const prefersReducedMotion = useReducedMotion();
    const percentage = Math.round((value / maxValue) * 100);

    // If user prefers reduced motion, render static bar
    if (prefersReducedMotion) {
        return (
            <div className={cn(height, 'bg-muted rounded-full overflow-hidden', className)}>
                <div
                    className={cn('h-full rounded-full', barClassName)}
                    style={{ width: `${percentage}%` }}
                />
            </div>
        );
    }

    return (
        <div className={cn(height, 'bg-muted rounded-full overflow-hidden', className)}>
            <motion.div
                className={cn('h-full rounded-full', barClassName)}
                initial={{ width: 0 }}
                animate={{ width: `${percentage}%` }}
                transition={{
                    delay,
                    duration: 0.4,
                    ease: 'easeOut',
                }}
            />
        </div>
    );
}
